import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconChartBar, IconRefresh, IconCopy, IconPlus, IconPencil, IconTrash,
  IconCurrencyWon, IconSettings, IconChevronUp, IconChevronDown, IconX, IconCheck,
  IconCalendar, IconNotes, IconCalculator, IconArrowUpRight, IconArrowDownRight,
} from '@tabler/icons-react';
import api from '../api/api';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';
import { ScreeningDetailModal } from './MarketAnalysisPage';
import { KrxDataDetailModal } from './KrxDataPage';

const fmt = (v) => Number(v || 0).toLocaleString();
const getKSTDate = () => {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
};

const diffEl = (v) => {
  const n = Number(v || 0);
  const color = n >= 0 ? '#e53e3e' : '#3182ce';
  return <span style={{ color, fontWeight: 600 }}>{n >= 0 ? '+' : ''}{fmt(n)}</span>;
};

const pctEl = (v) => {
  const n = Number(v || 0);
  const color = n >= 0 ? '#e53e3e' : '#3182ce';
  return <span style={{ color, fontWeight: 600 }}>{n >= 0 ? '+' : ''}{n.toFixed(2)}%</span>;
};

/* ── API 호출 함수 ──────────────────────────────── */
const stockApi = {
  getCompanies: () => api.get('/stock/companies').then(r => r.data),
  createCompany: (name) => api.post('/stock/companies', { name }).then(r => r.data),
  updateCompany: (id, name) => api.put(`/stock/companies/${id}`, { name }).then(r => r.data),
  deleteCompany: (id) => api.delete(`/stock/companies/${id}`),
  sortCompanies: (ids) => api.put('/stock/companies/sort', ids),

  getHoldings: (date) => api.get('/stock/holdings', { params: { date } }).then(r => r.data),
  getAvailableDates: () => api.get('/stock/holdings/dates').then(r => r.data),
  getNoPriceDates: () => api.get('/stock/holdings/no-price-dates').then(r => r.data),
  saveHolding: (data) => api.post('/stock/holdings', data).then(r => r.data),
  deleteHolding: (id) => api.delete(`/stock/holdings/${id}`),
  copyFromPrevious: (targetDate) => api.post('/stock/holdings/copy', null, { params: { targetDate } }).then(r => r.data),
  fetchPrices: (date) => api.post('/stock/holdings/fetch-prices', null, { params: { date } }).then(r => r.data),
  deleteByDate: (date) => api.delete('/stock/holdings/by-date', { params: { date } }).then(r => r.data),

  saveDeposit: (data) => api.post('/stock/deposits', data).then(r => r.data),

  searchStocks: (keyword) => api.get('/stock/search', { params: { keyword } }).then(r => r.data),
  searchStocksNaver: (keyword) => api.get('/stock/search/naver', { params: { keyword } }).then(r => r.data),
  getMarketIndex: (date) => api.get('/stock/holdings/market-index', { params: { date } }).then(r => r.data),
  getChartData: (limit) => api.get('/stock/holdings/chart-data', { params: { limit } }).then(r => r.data),
  getChartRange: () => api.get('/stock/holdings/chart-range').then(r => r.data),

  getMemo: (date) => api.get('/stock/memos', { params: { date } }).then(r => r.data),
  saveMemo: (date, content) => api.post('/stock/memos', { date, content }).then(r => r.data),
  deleteMemo: (date) => api.delete('/stock/memos', { params: { date } }),
  getAllMemos: () => api.get('/stock/memos/all').then(r => r.data),
  getCurrentPrices: (codes) => api.get('/stock/holdings/current-prices', { params: { codes: codes.join(',') } }).then(r => r.data),
  getTradeHistory: (date, companyId) => api.get('/stock/holdings/trade-history', { params: { date, ...(companyId ? { companyId } : {}) } }).then(r => r.data),
  getAllTradeHistory: (companyId) => api.get('/stock/holdings/trade-history/all', { params: companyId ? { companyId } : {} }).then(r => r.data),
};

/* ── 거래내역 전체보기 팝업 ─────────────────────── */
function TradeHistoryModal({ open, onClose, onDateClick, companyId }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    stockApi.getAllTradeHistory(companyId || null)
      .then(setGroups)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, companyId]);

  if (!open) return null;

  const total = groups.reduce((s, g) => s + g.trades.length, 0);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 60, paddingBottom: 40,
        overflowY: 'auto',
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#0f0f2a', borderRadius: 14, width: 600, maxWidth: '95vw',
        boxShadow: '0 24px 64px rgba(0,0,0,.6)',
        border: '1px solid #2a2a4a',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: '16px 20px 14px',
          borderBottom: '1px solid #2a2a4a',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconChartBar size={18} style={{ color: '#6b7db3' }} />
            <span style={{ color: '#e0e0ff', fontWeight: 700, fontSize: 16 }}>거래내역 전체보기</span>
            {!loading && total > 0 && (
              <span style={{ color: '#555', fontSize: 12 }}>({total}건)</span>
            )}
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: '2px 6px', borderRadius: 4 }}>
            <IconX size={18} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
          {loading && (
            <div style={{ color: '#555', textAlign: 'center', padding: '40px 0', fontSize: 14 }}>불러오는 중...</div>
          )}
          {!loading && groups.length === 0 && (
            <div style={{ color: '#444', textAlign: 'center', padding: '40px 0', fontSize: 14 }}>거래내역이 없습니다.</div>
          )}
          {!loading && groups.length > 0 && (
            <div style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute', left: 7, top: 8, bottom: 8,
                width: 2, background: '#2a2a4a', borderRadius: 1,
              }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                {groups.map((g, gi) => (
                  <div key={gi} style={{ display: 'flex', gap: 18, position: 'relative' }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%',
                      background: '#1a1a2e', border: '2px solid #4e9af1',
                      flexShrink: 0, marginTop: 2,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <button
                        type="button"
                        onClick={() => { onDateClick(g.date); onClose(); }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#4e9af1', fontSize: 13, fontWeight: 700, padding: 0,
                          marginBottom: 8, display: 'inline-block',
                        }}
                      >
                        {g.date}
                      </button>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {g.trades.map((t, ti) => (
                          <TradeRow key={ti} trade={t} />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TradeRow({ trade }) {
  const isBuy = trade.type === '매수';
  const color = isBuy ? '#e53e3e' : '#3182ce';
  const Icon = isBuy ? IconArrowUpRight : IconArrowDownRight;
  return (
    <div style={{
      background: '#1a1a2e', borderRadius: 8,
      padding: '8px 12px',
      border: `1px solid ${isBuy ? '#3a1a1a' : '#1a1a3a'}`,
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <Icon size={16} style={{ color, flexShrink: 0 }} />
      {/* PC: 매수/매도 텍스트 표시, 모바일: 숨김 */}
      <span className="d-none d-sm-inline" style={{ color, fontWeight: 700, fontSize: 12, width: 30 }}>{trade.type}</span>
      <span style={{ color: '#ddd', fontSize: 13, fontWeight: 600, flex: 1 }}>
        {trade.stockName}
        {trade.isNew && <span style={{ color: '#fbbf24', fontSize: 11, marginLeft: 5 }}>신규</span>}
      </span>
      <span style={{ color: '#aaa', fontSize: 12 }}>{Number(trade.quantity).toLocaleString()}주</span>
      {/* PC: 평균 텍스트 포함, 모바일: 숨김 */}
      <span className="d-none d-sm-inline" style={{ color: '#666', fontSize: 11, marginLeft: 4 }}>평균 {Number(trade.avgPrice).toLocaleString()}</span>
      {/* 모바일: 평균값과 증권사명 세로 배치 */}
      <span className="d-sm-none" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        <span style={{ color: '#666', fontSize: 11 }}>{Number(trade.avgPrice).toLocaleString()}</span>
        <span style={{ color: '#444', fontSize: 10 }}>{trade.companyName}</span>
      </span>
      {/* PC: 증권사명 */}
      <span className="d-none d-sm-inline" style={{ color: '#444', fontSize: 11 }}>{trade.companyName}</span>
    </div>
  );
}

/* ── 날짜별 거래내역 박스 ────────────────────────── */
function DailyTradeBox({ date, hasHoldings, onAllTradeOpen, companyId }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date || !hasHoldings) { setTrades([]); return; }
    setLoading(true);
    stockApi.getTradeHistory(date, companyId || null)
      .then(setTrades)
      .catch(() => setTrades([]))
      .finally(() => setLoading(false));
  }, [date, hasHoldings, companyId]);

  if (!hasHoldings) return null;

  return (
    <div className="card mb-3" style={{ background: '#1a1a2e', border: 'none' }}>
      <div className="card-body py-2 px-3">
        <div className="d-flex align-items-start gap-2">
          <IconChartBar size={15} style={{ color: '#6b7db3', flexShrink: 0, marginTop: 3 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {loading ? (
              <span style={{ color: '#555', fontSize: 13 }}>불러오는 중...</span>
            ) : trades.length === 0 ? (
              <span style={{ color: '#555', fontSize: 13 }}>거래내역 없음 (이전 날짜 대비 변동 없음)</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {trades.map((t, i) => <TradeRow key={i} trade={t} />)}
              </div>
            )}
          </div>
          <button type="button" onClick={onAllTradeOpen}
            title="거래내역 전체보기"
            style={{
              background: 'none', border: '1px solid #2a2a4a', borderRadius: 5,
              color: '#4a5a8a', cursor: 'pointer', padding: '2px 8px',
              fontSize: 11, display: 'flex', alignItems: 'center', gap: 3,
              flexShrink: 0, whiteSpace: 'nowrap',
            }}>
            <IconChartBar size={11} />
            전체보기
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── 메모 타임라인 팝업 ─────────────────────────── */
function MemoTimelineModal({ open, onClose, onDateClick }) {
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    stockApi.getAllMemos()
      .then(setMemos)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 60, paddingBottom: 40,
        overflowY: 'auto',
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#0f0f2a', borderRadius: 14, width: 560, maxWidth: '95vw',
        boxShadow: '0 24px 64px rgba(0,0,0,.6)',
        border: '1px solid #2a2a4a',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* 헤더 */}
        <div style={{
          padding: '16px 20px 14px',
          borderBottom: '1px solid #2a2a4a',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconNotes size={18} style={{ color: '#6b7db3' }} />
            <span style={{ color: '#e0e0ff', fontWeight: 700, fontSize: 16 }}>메모 타임라인</span>
            {!loading && memos.length > 0 && (
              <span style={{ color: '#555', fontSize: 12 }}>({memos.length}건)</span>
            )}
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: '2px 6px', borderRadius: 4 }}>
            <IconX size={18} />
          </button>
        </div>

        {/* 컨텐츠 */}
        <div style={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
          {loading && (
            <div style={{ color: '#555', textAlign: 'center', padding: '40px 0', fontSize: 14 }}>
              불러오는 중...
            </div>
          )}
          {!loading && memos.length === 0 && (
            <div style={{ color: '#444', textAlign: 'center', padding: '40px 0', fontSize: 14 }}>
              저장된 메모가 없습니다.
            </div>
          )}
          {!loading && memos.length > 0 && (
            <div style={{ position: 'relative' }}>
              {/* 타임라인 세로선 */}
              <div style={{
                position: 'absolute', left: 7, top: 8, bottom: 8,
                width: 2, background: '#2a2a4a', borderRadius: 1,
              }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {memos.map((m, i) => (
                  <div key={i} style={{ display: 'flex', gap: 18, position: 'relative' }}>
                    {/* 타임라인 점 */}
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%',
                      background: '#1a1a2e', border: '2px solid #4e9af1',
                      flexShrink: 0, marginTop: 2,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* 날짜 */}
                      <button
                        type="button"
                        onClick={() => { onDateClick(m.date); onClose(); }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#4e9af1', fontSize: 13, fontWeight: 700, padding: 0,
                          marginBottom: 6, display: 'inline-block',
                        }}
                        title="이 날짜로 이동"
                      >
                        {m.date}
                      </button>
                      {/* 메모 내용 */}
                      <div style={{
                        background: '#1a1a2e', borderRadius: 8,
                        padding: '10px 14px',
                        color: '#ccc', fontSize: 13, lineHeight: 1.7,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        border: '1px solid #2a2a4a',
                      }}>
                        {m.content}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── 날짜별 메모 박스 ───────────────────────────── */
function DailyMemoBox({ date, showToast, onTimelineOpen }) {
  const [content, setContent] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  // 날짜 변경 시 메모 조회
  useEffect(() => {
    if (!date) return;
    stockApi.getMemo(date)
      .then(data => setContent(data.content || ''))
      .catch(() => setContent(''));
    setEditing(false);
  }, [date]);

  const startEdit = () => {
    setDraft(content);
    setEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleCancel = () => {
    setEditing(false);
    setDraft('');
  };

  const handleSave = async () => {
    if (!draft.trim()) {
      // 내용 비우면 삭제
      await handleDelete();
      return;
    }
    setSaving(true);
    try {
      const saved = await stockApi.saveMemo(date, draft.trim());
      setContent(saved.content);
      setEditing(false);
      showToast('메모가 저장되었습니다.');
    } catch {
      showToast('메모 저장 실패', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await stockApi.deleteMemo(date);
      setContent('');
      setEditing(false);
      showToast('메모가 삭제되었습니다.');
    } catch {
      showToast('메모 삭제 실패', 'error');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  return (
    <div className="card mb-3" style={{ background: '#1a1a2e', border: 'none' }}>
      <div className="card-body py-2 px-3">
        <div className="d-flex align-items-start gap-2">
          <IconNotes size={15} style={{ color: '#6b7db3', flexShrink: 0, marginTop: 3 }} />
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            {editing ? (
              <div>
                <textarea
                  ref={textareaRef}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={3}
                  placeholder="이 날짜에 대한 메모를 입력하세요... (Ctrl+Enter 저장, Esc 취소)"
                  style={{
                    width: '100%', background: '#0f0f2a', border: '1px solid #2a2a4a',
                    borderRadius: 6, color: '#ddd', fontSize: 13, padding: '6px 10px',
                    resize: 'vertical', outline: 'none', fontFamily: 'inherit',
                    lineHeight: 1.6,
                  }}
                />
                <div className="d-flex gap-2 mt-1 justify-content-end">
                  <button type="button" onClick={handleCancel}
                    style={{ background: 'none', border: '1px solid #2a2a4a', borderRadius: 5,
                      color: '#888', cursor: 'pointer', padding: '3px 12px', fontSize: 12 }}>
                    취소
                  </button>
                  <button type="button" onClick={handleSave} disabled={saving}
                    style={{ background: '#206bc4', border: 'none', borderRadius: 5,
                      color: '#fff', cursor: 'pointer', padding: '3px 14px', fontSize: 12, fontWeight: 600 }}>
                    {saving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </div>
            ) : content ? (
              <div className="d-flex align-items-start gap-2" style={{ minWidth: 0 }}>
                <span style={{ color: '#ccc', fontSize: 13, flex: 1, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {content}
                </span>
                <div className="d-flex gap-1 flex-shrink-0">
                  <button type="button" onClick={startEdit}
                    style={{ background: 'none', border: 'none', color: '#6b7db3', cursor: 'pointer', padding: '2px 5px', borderRadius: 4 }}
                    title="메모 수정">
                    <IconPencil size={13} />
                  </button>
                  <button type="button" onClick={handleDelete}
                    style={{ background: 'none', border: 'none', color: '#7a3a3a', cursor: 'pointer', padding: '2px 5px', borderRadius: 4 }}
                    title="메모 삭제">
                    <IconTrash size={13} />
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={startEdit}
                style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer',
                  fontSize: 13, padding: 0, textAlign: 'left' }}>
                + 이 날짜에 메모 추가...
              </button>
            )}
          </div>
          <button type="button" onClick={onTimelineOpen}
            title="메모 타임라인 전체보기"
            style={{
              background: 'none', border: '1px solid #2a2a4a', borderRadius: 5,
              color: '#4a5a8a', cursor: 'pointer', padding: '2px 8px',
              fontSize: 11, display: 'flex', alignItems: 'center', gap: 3,
              flexShrink: 0, whiteSpace: 'nowrap',
            }}>
            <IconNotes size={11} />
            전체보기
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── 자산 추이 차트 ─────────────────────────────── */
const CHART_SERIES = [
  { key: 'eval',    label: '보유주식',  color: '#4e9af1' },
  { key: 'deposit', label: '예수금',    color: '#a78bfa' },
  { key: 'asset',   label: '총자산',    color: '#34d399' },
  { key: 'profit',  label: '평가손익',  color: '#f87171', axis: 'left2' },
  { key: 'kospi',   label: '코스피',    color: '#fbbf24', axis: 'right' },
  { key: 'kosdaq',  label: '코스닥',    color: '#fb923c', axis: 'right2' },
];

const DEFAULT_PERIODS = [{ v: 5, l: '5일' }, { v: 10, l: '10일' }, { v: 30, l: '30일' }, { v: 0, l: '전체' }];

function AssetChart({ showToast }) {
  const [periods, setPeriods] = useState(DEFAULT_PERIODS);
  const [limit, setLimit] = useState(5);
  const [rows, setRows] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [hidden, setHidden] = useState(new Set(['eval', 'deposit', 'kosdaq']));
  const [tooltip, setTooltip] = useState(null);
  const [hideAxis, setHideAxis] = useState(false);

  // stock_prices 최초 수집일 ~ 오늘까지 총 일수(n) 기준으로 기간 버튼 계산
  useEffect(() => {
    stockApi.getChartRange().then(({ totalDays }) => {
      const n = Math.max(Number(totalDays) || 0, 0);
      if (n <= 0) return;
      const list = [10, 6, 4, 3, 2].map(d => {
        const days = Math.max(1, Math.round(n / d));
        return { v: days, l: `${days}일` };
      });
      list.push({ v: 0, l: '전체' });
      setPeriods(list);
      setLimit(list[0].v);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setChartLoading(true);
    stockApi.getChartData(limit === 0 ? 9999 : limit)
      .then(setRows)
      .catch(() => showToast('차트 데이터 로딩 실패', 'error'))
      .finally(() => setChartLoading(false));
  }, [limit, showToast]);

  const ChartLoadingOverlay = () => chartLoading ? (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 5, borderRadius: 4,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <div className="spinner-border spinner-border-sm text-primary" role="status" />
        <div style={{ color: '#ccc', fontSize: 12 }}>불러오는 중...</div>
      </div>
    </div>
  ) : null;

  if (rows.length < 2) return (
    <div className="card mb-3" style={{ background: '#1a1a2e', border: 'none', position: 'relative' }}>
      <ChartLoadingOverlay />
      <div className="card-body py-3 text-center" style={{ color: '#555', fontSize: 13 }}>
        차트를 표시하려면 2일 이상의 데이터가 필요합니다.
      </div>
    </div>
  );

  const W = 900, H = 220, PAD = hideAxis
    ? { top: 16, right: 8, bottom: 36, left: 8 }
    : { top: 16, right: 110, bottom: 36, left: 110 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;
  const n = rows.length;

  // 좌축1: eval/deposit/asset, 좌축2: profit, 우축: kospi, 우축2: kosdaq
  const leftKeys   = CHART_SERIES.filter(s => !s.axis           && !hidden.has(s.key)).map(s => s.key);
  const left2Keys  = CHART_SERIES.filter(s => s.axis === 'left2'  && !hidden.has(s.key)).map(s => s.key);
  const rightKeys  = CHART_SERIES.filter(s => s.axis === 'right'  && !hidden.has(s.key)).map(s => s.key);
  const right2Keys = CHART_SERIES.filter(s => s.axis === 'right2' && !hidden.has(s.key)).map(s => s.key);

  const getVal = (row, key) => row[key] != null ? Number(row[key]) : null;

  const allLeft   = rows.flatMap(r => leftKeys.map(k => getVal(r, k))).filter(v => v != null);
  const allLeft2  = rows.flatMap(r => left2Keys.map(k => getVal(r, k))).filter(v => v != null);
  const allRight  = rows.flatMap(r => rightKeys.map(k => getVal(r, k))).filter(v => v != null);
  const allRight2 = rows.flatMap(r => right2Keys.map(k => getVal(r, k))).filter(v => v != null);

  const minL  = allLeft.length   ? Math.min(...allLeft)   : 0;
  const maxL  = allLeft.length   ? Math.max(...allLeft)   : 1;
  const minL2 = allLeft2.length  ? Math.min(...allLeft2)  : 0;
  const maxL2 = allLeft2.length  ? Math.max(...allLeft2)  : 1;
  const minR  = allRight.length  ? Math.min(...allRight)  : 0;
  const maxR  = allRight.length  ? Math.max(...allRight)  : 1;
  const minR2 = allRight2.length ? Math.min(...allRight2) : 0;
  const maxR2 = allRight2.length ? Math.max(...allRight2) : 1;

  const padRange = (mn, mx) => {
    const diff = mx - mn || Math.abs(mx) || 1;
    return [mn - diff * 0.08, mx + diff * 0.08];
  };
  const [lMin,  lMax]  = padRange(minL,  maxL);
  const [lMin2, lMax2] = padRange(minL2, maxL2);
  const [rMin,  rMax]  = padRange(minR,  maxR);
  const [rMin2, rMax2] = padRange(minR2, maxR2);

  const xPos   = (i) => PAD.left + (n === 1 ? cW / 2 : (i / (n - 1)) * cW);
  const yPosL  = (v) => PAD.top + cH - ((v - lMin)  / (lMax  - lMin))  * cH;
  const yPosL2 = (v) => PAD.top + cH - ((v - lMin2) / (lMax2 - lMin2)) * cH;
  const yPosR  = (v) => PAD.top + cH - ((v - rMin)  / (rMax  - rMin))  * cH;
  const yPosR2 = (v) => PAD.top + cH - ((v - rMin2) / (rMax2 - rMin2)) * cH;
  const yPos = (key, v) => {
    const s = CHART_SERIES.find(s => s.key === key);
    if (s?.axis === 'right2') return yPosR2(v);
    if (s?.axis === 'right')  return yPosR(v);
    if (s?.axis === 'left2')  return yPosL2(v);
    return yPosL(v);
  };

  const makePath = (key, yFn) => {
    const pts = rows.map((r, i) => {
      const v = getVal(r, key);
      return v != null ? `${xPos(i)},${yFn(v)}` : null;
    }).filter(Boolean);
    if (pts.length < 2) return '';
    return 'M' + pts.join('L');
  };

  const fmtAxis = (v) => {
    const abs = Math.abs(v);
    if (abs >= 1_0000_0000) return (v / 1_0000_0000).toFixed(1) + '억';
    if (abs >= 10000) return (v / 10000).toFixed(0) + '만';
    return v.toFixed(0);
  };

  // Y축 틱 5개
  const ticks = (mn, mx) => Array.from({ length: 5 }, (_, i) => mn + (mx - mn) * i / 4);

  const handleMouseMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (W / rect.width) - PAD.left;
    if (mx < 0 || mx > cW) { setTooltip(null); return; }
    const idx = Math.min(n - 1, Math.max(0, Math.round(mx / cW * (n - 1))));
    setTooltip({ idx, x: xPos(idx), y: PAD.top + cH / 2 });
  };

  return (
    <div className="card mb-3" style={{ background: '#1a1a2e', border: 'none', position: 'relative' }}>
      <ChartLoadingOverlay />
      <div className="card-body py-3 px-3">
        {/* 기간 선택 */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div style={{ color: '#aaa', fontSize: 12, fontWeight: 600 }}>자산 추이</div>
          <div className="d-flex gap-1">
            {periods.map(({ v, l }) => (
              <button key={v} type="button"
                onClick={() => setLimit(v)}
                style={{
                  fontSize: 11, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                  background: limit === v ? '#4e9af1' : '#2a2a4a',
                  color: limit === v ? '#fff' : '#aaa', fontWeight: limit === v ? 700 : 400,
                }}>{l}</button>
            ))}
            <button type="button"
              onClick={() => setHideAxis(prev => !prev)}
              style={{
                fontSize: 11, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                background: hideAxis ? '#4e9af1' : '#2a2a4a',
                color: hideAxis ? '#fff' : '#aaa', fontWeight: hideAxis ? 700 : 400,
              }}>$</button>
          </div>
        </div>

        {/* 범례 */}
        <div className="d-flex gap-3 flex-wrap mb-2">
          {CHART_SERIES.map(s => (
            <button key={s.key} type="button" onClick={() => setHidden(prev => {
              const next = new Set(prev);
              next.has(s.key) ? next.delete(s.key) : next.add(s.key);
              return next;
            })} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'center', gap: 4, opacity: hidden.has(s.key) ? 0.35 : 1,
            }}>
              <span style={{ display: 'inline-block', width: 24, height: 2, background: s.color, borderRadius: 1 }} />
              <span style={{ color: '#ccc', fontSize: 11 }}>{s.label}</span>
              {s.axis === 'left2'  && <span style={{ color: '#f87171', fontSize: 10 }}>(좌2)</span>}
              {s.axis === 'right'  && <span style={{ color: '#fbbf24', fontSize: 10 }}>(우1)</span>}
              {s.axis === 'right2' && <span style={{ color: '#fb923c', fontSize: 10 }}>(우2)</span>}
            </button>
          ))}
        </div>

        {/* SVG 차트 */}
        <div style={{ overflowX: 'auto' }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 320, display: 'block' }}
            onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>

            {/* 그리드 */}
            {ticks(lMin, lMax).map((v, i) => (
              <line key={i} x1={PAD.left} x2={W - PAD.right}
                y1={yPosL(v)} y2={yPosL(v)}
                stroke="#2a2a4a" strokeWidth={1} />
            ))}

            {/* 좌축1 레이블 (보유주식/예수금/총자산) */}
            {!hideAxis && leftKeys.length > 0 && ticks(lMin, lMax).map((v, i) => (
              <text key={i} x={PAD.left - 4} y={yPosL(v) + 4}
                textAnchor="end" fill="#666" fontSize={8}>{fmtAxis(v)}</text>
            ))}

            {/* 좌축2 레이블 (평가손익) */}
            {!hideAxis && left2Keys.length > 0 && ticks(lMin2, lMax2).map((v, i) => (
              <text key={i} x={PAD.left - 54} y={yPosL2(v) + 4}
                textAnchor="end" fill="#f87171" fontSize={8}>{fmtAxis(v)}</text>
            ))}

            {/* 우축 레이블 (코스피) */}
            {!hideAxis && rightKeys.length > 0 && ticks(rMin, rMax).map((v, i) => (
              <text key={i} x={W - PAD.right + 4} y={yPosR(v) + 4}
                textAnchor="start" fill="#fbbf24" fontSize={8}>{fmtAxis(v)}</text>
            ))}

            {/* 우축2 레이블 (코스닥) */}
            {!hideAxis && right2Keys.length > 0 && ticks(rMin2, rMax2).map((v, i) => (
              <text key={i} x={W - PAD.right + 54} y={yPosR2(v) + 4}
                textAnchor="start" fill="#fb923c" fontSize={8}>{fmtAxis(v)}</text>
            ))}

            {/* X축 날짜 */}
            {rows.map((r, i) => {
              const show = n <= 10 || i % Math.ceil(n / 10) === 0 || i === n - 1;
              return show ? (
                <text key={i} x={xPos(i)} y={H - PAD.bottom + 14}
                  textAnchor="middle" fill="#555" fontSize={10}>
                  {r.date.slice(5)}
                </text>
              ) : null;
            })}

            {/* 라인 */}
            {CHART_SERIES.filter(s => !hidden.has(s.key)).map(s => {
              const yFn = s.axis === 'right2' ? yPosR2 : s.axis === 'right' ? yPosR : s.axis === 'left2' ? yPosL2 : yPosL;
              const path = makePath(s.key, yFn);
              return path ? (
                <path key={s.key} d={path} fill="none"
                  stroke={s.color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
              ) : null;
            })}

            {/* 데이터 포인트 */}
            {CHART_SERIES.filter(s => !hidden.has(s.key)).map(s =>
              rows.map((r, i) => {
                const v = getVal(r, s.key);
                if (v == null) return null;
                const yFn = s.axis === 'right2' ? yPosR2 : s.axis === 'right' ? yPosR : s.axis === 'left2' ? yPosL2 : yPosL;
                return (
                  <circle key={`${s.key}-${i}`}
                    cx={xPos(i)} cy={yFn(v)} r={3}
                    fill={s.color} stroke="#1a1a2e" strokeWidth={1.5} />
                );
              })
            )}

            {/* 툴팁 수직선 */}
            {tooltip && (
              <>
                <line x1={tooltip.x} x2={tooltip.x} y1={PAD.top} y2={PAD.top + cH}
                  stroke="#ffffff33" strokeWidth={1} strokeDasharray="4 2" />
                {/* 툴팁 박스 */}
                {(() => {
                  const row = rows[tooltip.idx];
                  const items = CHART_SERIES.filter(s => !hidden.has(s.key) && getVal(row, s.key) != null);
                  const bx = tooltip.x + 12 > W - 180 ? tooltip.x - 165 : tooltip.x + 12;
                  const by = PAD.top;
                  const bh = 20 + items.length * 16;
                  return (
                    <g>
                      <rect x={bx} y={by} width={155} height={bh} rx={5} fill="#0f0f2a" opacity={0.95} />
                      <text x={bx + 8} y={by + 13} fill="#aaa" fontSize={10}>{row.date}</text>
                      {items.map((s, ii) => {
                        const v = Number(getVal(row, s.key));
                        const formatted = (s.axis === 'right' || s.axis === 'right2')
                          ? v.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : (Math.abs(v) >= 100000000 ? (v / 100000000).toFixed(1) + '억' : Math.abs(v) >= 10000 ? (v / 10000).toFixed(0) + '만' : v.toFixed(0));
                        return (
                          <g key={s.key}>
                            <rect x={bx + 8} y={by + 20 + ii * 16} width={8} height={2} fill={s.color} />
                            <text x={bx + 20} y={by + 28 + ii * 16} fill="#ccc" fontSize={10}>
                              {s.label}: {v >= 0 ? '' : '-'}{formatted}
                            </text>
                          </g>
                        );
                      })}
                    </g>
                  );
                })()}
              </>
            )}
          </svg>
        </div>
      </div>
    </div>
  );
}

/* ── 종가수집 진행 오버레이 ─────────────────────── */
function FetchProgressOverlay({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [done, setDone] = useState(null); // { success, fail } or { stopped: true }
  const [stopping, setStopping] = useState(false);
  const logEndRef = useRef(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // useCallback으로 안정화 — 인라인 함수면 매 렌더마다 새 레퍼런스 생성 → SSE 재연결 루프
  const handleLog = useCallback((text, color) => setLogs(prev => [...prev, { text, color }]), []);
  const handleProgress = useCallback((cur, total) => setProgress({ current: cur, total }), []);
  const handleDone = useCallback((result) => setDone(result), []);

  const handleStop = async () => {
    setStopping(true);
    try { await api.post('/stock/holdings/fetch-prices-stop'); } catch { /* 무시 */ }
  };

  const handleClose = () => onClose(done);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, width: 480, maxWidth: '95vw',
        boxShadow: '0 20px 60px rgba(0,0,0,.4)', overflow: 'hidden',
      }}>
        {/* 헤더 */}
        <div style={{
          background: done ? (done.fail === 0 ? '#206bc4' : '#f59e0b') : '#1a1a2e',
          padding: '16px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ color: '#fff' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {done?.stopped ? '종가 수집 중단됨' : done ? '종가 수집 완료' : stopping ? '중단 요청 중...' : '종가 수집 중...'}
            </div>
            {!done && progress.total > 0 && (
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                {progress.current} / {progress.total} 종목 처리 중
              </div>
            )}
            {done && !done.stopped && (
              <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
                성공 {done.success}건 / 실패 {done.fail}건
              </div>
            )}
            {done?.stopped && (
              <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
                중단됨 — 성공 {done.success}건 / 실패 {done.fail ?? 0}건
              </div>
            )}
          </div>
          <div className="d-flex gap-2">
            {!done && (
              <button type="button" onClick={handleStop} disabled={stopping}
                style={{ background: stopping ? 'rgba(255,255,255,0.1)' : '#e53e3e', border: 'none',
                  borderRadius: 6, color: '#fff', cursor: stopping ? 'default' : 'pointer',
                  padding: '4px 14px', fontSize: 13, fontWeight: 600 }}>
                {stopping ? '중단 중...' : '■ 중지'}
              </button>
            )}
            {done && (
              <button type="button" onClick={handleClose}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6,
                  color: '#fff', cursor: 'pointer', padding: '4px 12px', fontSize: 13 }}>
                닫기
              </button>
            )}
          </div>
        </div>

        {/* 프로그레스 바 */}
        {progress.total > 0 && (
          <div style={{ height: 4, background: '#e6e7e9' }}>
            <div style={{
              height: '100%',
              width: `${Math.round(progress.current / progress.total * 100)}%`,
              background: done ? '#2fb344' : '#206bc4',
              transition: 'width .3s ease',
            }} />
          </div>
        )}

        {/* 로그 */}
        <div style={{
          height: 280, overflowY: 'auto', padding: '10px 16px',
          background: '#0d1117', fontFamily: 'monospace', fontSize: 12,
        }}>
          {logs.length === 0 && (
            <div style={{ color: '#666', paddingTop: 8 }}>연결 중...</div>
          )}
          {logs.map((log, i) => (
            <div key={i} style={{ color: log.color, lineHeight: 1.6 }}>{log.text}</div>
          ))}
          <div ref={logEndRef} />
        </div>
      </div>

      {/* SSE 연결 처리 */}
      <FetchSseRunner
        onLog={handleLog}
        onProgress={handleProgress}
        onDone={handleDone}
      />
    </div>
  );
}

function FetchSseRunner({ onLog, onProgress, onDone }) {
  const dateRef = useRef(window.__fetchDate);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
    const url = `${baseUrl}/stock/holdings/fetch-prices-stream?date=${dateRef.current}`;

    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) { onLog('❌ 연결 실패', '#e53e3e'); return; }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let receivedDone = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();
          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            try {
              const data = JSON.parse(line.slice(5).trim());
              if (data.type === 'start') {
                onLog(`▶ 총 ${data.total}개 종목 수집 시작`, '#58a6ff');
                onProgress(0, data.total);
              } else if (data.type === 'progress') {
                onProgress(data.current, data.total);
                if (data.status === 'ok') {
                  onLog(`✓ [${data.current}/${data.total}] ${data.name} (${data.code}) — ${Number(data.price).toLocaleString()}원`, '#3fb950');
                } else if (data.status === 'none') {
                  onLog(`– [${data.current}/${data.total}] ${data.name} (${data.code}) — 가격 없음`, '#8b949e');
                } else {
                  onLog(`✗ [${data.current}/${data.total}] ${data.name} (${data.code}) — 오류`, '#f85149');
                }
              } else if (data.type === 'done') {
                onLog(``, '#555');
                onLog(`완료 ─ 성공 ${data.success}건 / 실패 ${data.fail}건`, '#58a6ff');
                onDone({ success: data.success, fail: data.fail });
                receivedDone = true;
              } else if (data.type === 'stopped') {
                onLog(``, '#555');
                onLog(`■ 중단됨 ─ 성공 ${data.success}건 / 실패 ${data.fail}건`, '#f59e0b');
                onDone({ stopped: true, success: data.success, fail: data.fail });
                receivedDone = true;
              } else if (data.type === 'error') {
                onLog(`❌ ${data.msg}`, '#e53e3e');
                onDone({ stopped: true, success: 0, current: 0 });
                receivedDone = true;
              }
            } catch { /* JSON 파싱 오류 무시 */ }
          }
        }
        // stream이 ended 됐는데 done/stopped 이벤트 못받은 경우 (연결 끊김)
        if (!receivedDone) {
          onLog('⚠ 연결이 끊겼습니다.', '#f59e0b');
          onDone({ stopped: true, success: 0, fail: 0 });
        }
      } catch (e) {
        if (e.name !== 'AbortError') onLog(`❌ 오류: ${e.message}`, '#e53e3e');
      }
    })();

    // 컴포넌트 언마운트(닫기) 시 fetch 즉시 중단 → 백엔드 onError 콜백 발동
    return () => controller.abort();
  }, [onLog, onProgress, onDone]);

  return null;
}

/* ── 커스텀 날짜 선택기 ─────────────────────────── */
const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

function StockDatePicker({ value, onChange, availableDates, noPriceDates }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => parseInt(value?.slice(0, 4) || new Date().getFullYear()));
  const [viewMonth, setViewMonth] = useState(() => parseInt(value?.slice(5, 7) || new Date().getMonth() + 1));
  const ref = useRef(null);

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // value 변경 시 viewYear/viewMonth 동기화
  useEffect(() => {
    if (value) {
      setViewYear(parseInt(value.slice(0, 4)));
      setViewMonth(parseInt(value.slice(5, 7)));
    }
  }, [value]);

  const prevMonth = () => {
    if (viewMonth === 1) { setViewYear(y => y - 1); setViewMonth(12); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 12) { setViewYear(y => y + 1); setViewMonth(1); }
    else setViewMonth(m => m + 1);
  };

  // 달력 날짜 계산
  const firstDay = new Date(viewYear, viewMonth - 1, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const toDateStr = (d) =>
    `${viewYear}-${String(viewMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const today = getKSTDate();
  const displayLabel = value
    ? `${value.slice(0, 4)}년 ${parseInt(value.slice(5, 7))}월 ${parseInt(value.slice(8, 10))}일`
    : '날짜 선택';

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
        style={{ minWidth: 150, justifyContent: 'flex-start', fontWeight: 500 }}
        onClick={() => setOpen(v => !v)}
      >
        <IconCalendar size={15} style={{ opacity: 0.6, flexShrink: 0 }} />
        <span>{displayLabel}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #e6e7e9', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: '12px 14px', minWidth: 280,
        }}>
          {/* 월 헤더 */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <button type="button" className="btn btn-ghost-secondary btn-sm btn-icon" onClick={prevMonth}>
              <IconChevronDown size={14} style={{ transform: 'rotate(90deg)' }} />
            </button>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{viewYear}년 {viewMonth}월</span>
            <button type="button" className="btn btn-ghost-secondary btn-sm btn-icon" onClick={nextMonth}>
              <IconChevronDown size={14} style={{ transform: 'rotate(-90deg)' }} />
            </button>
          </div>

          {/* 요일 헤더 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {DAY_NAMES.map((d, i) => (
              <div key={d} style={{
                textAlign: 'center', fontSize: 11, fontWeight: 600, padding: '2px 0',
                color: i === 0 ? '#e53e3e' : i === 6 ? '#3182ce' : '#888',
              }}>{d}</div>
            ))}
          </div>

          {/* 날짜 셀 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((d, i) => {
              if (!d) return <div key={`e${i}`} />;
              const dateStr = toDateStr(d);
              const isSelected = dateStr === value;
              const isToday = dateStr === today;
              const hasData = availableDates.has(dateStr);
              const noPrice = noPriceDates?.has(dateStr);
              const dow = (firstDay + d - 1) % 7;
              // 점 색상: 종가 없으면 빨간색, 종가 있으면 파란색, 데이터 없으면 점 없음
              const dotColor = noPrice ? '#e53e3e' : hasData ? '#206bc4' : null;
              const dotColorSelected = noPrice ? '#ffb3b3' : hasData ? '#fff' : null;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => { onChange(dateStr); setOpen(false); }}
                  style={{
                    border: 'none',
                    borderRadius: 6,
                    padding: '5px 2px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: 13,
                    position: 'relative',
                    background: isSelected ? '#206bc4' : isToday ? '#e8f0fe' : 'transparent',
                    color: isSelected ? '#fff' : dow === 0 ? '#e53e3e' : dow === 6 ? '#3182ce' : '#333',
                    fontWeight: isSelected || isToday ? 700 : 400,
                  }}
                >
                  {d}
                  {(dotColor || dotColorSelected) ? (
                    <span style={{
                      display: 'block', width: 5, height: 5, borderRadius: '50%',
                      background: isSelected ? dotColorSelected : dotColor,
                      margin: '1px auto 0',
                    }} />
                  ) : (
                    <span style={{ display: 'block', height: 6 }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* 오늘 버튼 */}
          <div className="mt-2 text-end">
            <button type="button" className="btn btn-sm btn-ghost-primary"
              style={{ fontSize: 12 }}
              onClick={() => { onChange(today); setOpen(false); }}>
              오늘
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── 증권사 설정 모달 ──────────────────────────── */
function CompanySettingsModal({ open, onClose, showToast, onChanged }) {
  const [companies, setCompanies] = useState([]);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await stockApi.getCompanies();
      setCompanies(data);
    } catch {
      showToast('증권사 목록 로딩 실패', 'error');
    }
  }, [showToast]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try {
      await stockApi.createCompany(newName.trim());
      setNewName('');
      await load();
      onChanged();
      showToast('증권사가 추가되었습니다.');
    } catch (e) {
      showToast(e?.response?.data?.message || '추가 실패', 'error');
    } finally { setLoading(false); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      await stockApi.updateCompany(id, editName.trim());
      setEditId(null);
      await load();
      onChanged();
      showToast('수정되었습니다.');
    } catch {
      showToast('수정 실패', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 증권사를 삭제하시겠습니까?')) return;
    try {
      await stockApi.deleteCompany(id);
      await load();
      onChanged();
      showToast('삭제되었습니다.');
    } catch (e) {
      showToast(e?.response?.data?.message || '삭제 실패', 'error');
    }
  };

  const handleMoveUp = async (idx) => {
    if (idx === 0) return;
    const newOrder = [...companies];
    [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
    setCompanies(newOrder);
    await stockApi.sortCompanies(newOrder.map(c => c.id));
    onChanged();
  };

  const handleMoveDown = async (idx) => {
    if (idx === companies.length - 1) return;
    const newOrder = [...companies];
    [newOrder[idx + 1], newOrder[idx]] = [newOrder[idx], newOrder[idx + 1]];
    setCompanies(newOrder);
    await stockApi.sortCompanies(newOrder.map(c => c.id));
    onChanged();
  };

  if (!open) return null;

  return (
    <div className="modal modal-blur fade show" style={{ display: 'block' }} onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, onClose)}>
      <div className="modal-dialog modal-sm modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center gap-2">
              <IconSettings size={18} /> 증권사 설정
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {/* 추가 입력 */}
            <div className="input-group mb-3">
              <input type="text" className="form-control form-control-sm"
                placeholder="증권사명 입력"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
              />
              <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={loading}>
                <IconPlus size={14} /> 추가
              </button>
            </div>

            {/* 증권사 목록 */}
            <div className="list-group list-group-flush">
              {companies.length === 0 && (
                <div className="text-muted text-center py-3" style={{ fontSize: 13 }}>
                  증권사를 추가하세요
                </div>
              )}
              {companies.map((c, idx) => (
                <div key={c.id} className="list-group-item px-0 py-2 d-flex align-items-center gap-2">
                  <div className="d-flex flex-column gap-0" style={{ marginRight: 2 }}>
                    <button className="btn btn-ghost-secondary btn-icon btn-sm p-0" style={{ lineHeight: 1 }}
                      onClick={() => handleMoveUp(idx)} disabled={idx === 0}>
                      <IconChevronUp size={12} />
                    </button>
                    <button className="btn btn-ghost-secondary btn-icon btn-sm p-0" style={{ lineHeight: 1 }}
                      onClick={() => handleMoveDown(idx)} disabled={idx === companies.length - 1}>
                      <IconChevronDown size={12} />
                    </button>
                  </div>

                  {editId === c.id ? (
                    <>
                      <input type="text" className="form-control form-control-sm flex-fill"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleUpdate(c.id); if (e.key === 'Escape') setEditId(null); }}
                        autoFocus
                      />
                      <button className="btn btn-success btn-icon btn-sm" onClick={() => handleUpdate(c.id)}>
                        <IconCheck size={14} />
                      </button>
                      <button className="btn btn-secondary btn-icon btn-sm" onClick={() => setEditId(null)}>
                        <IconX size={14} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-fill" style={{ fontSize: 14 }}>{c.name}</span>
                      <button className="btn btn-ghost-secondary btn-icon btn-sm"
                        onClick={() => { setEditId(c.id); setEditName(c.name); }}>
                        <IconPencil size={14} />
                      </button>
                      <button className="btn btn-ghost-danger btn-icon btn-sm"
                        onClick={() => handleDelete(c.id)}>
                        <IconTrash size={14} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>닫기</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 잔고 입력 모달 ────────────────────────────── */
function HoldingModal({ open, onClose, onSaved, showToast, companies, date, editData }) {
  const [form, setForm] = useState({ companyId: '', stockName: '', stockCode: '', quantity: '', avgPrice: '', currentPrice: '', memo: '' });
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (editData) {
        setForm({
          companyId: editData.companyId || '',
          stockName: editData.stockName || '',
          stockCode: editData.stockCode || '',
          quantity: editData.quantity || '',
          avgPrice: editData.avgPrice || '',
          currentPrice: (editData.currentPrice && Number(editData.currentPrice) > 0) ? editData.currentPrice : '',
          memo: editData.memo || '',
        });
      } else {
        setForm({ companyId: companies[0]?.id || '', stockName: '', stockCode: '', quantity: '', avgPrice: '', currentPrice: '', memo: '' });
      }
      setSuggestions([]);
      setSearching(false);
    }
  }, [open, editData, companies]);

  const handleStockNameChange = (value) => {
    setForm(f => ({ ...f, stockName: value, stockCode: '' }));
    setSuggestions([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 1) return;
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        // 1순위: 네이버 금융 전체 종목
        let results = await stockApi.searchStocksNaver(value).catch(() => []);
        // 2순위: 내 DB (네이버 결과 없을 때 폴백)
        if (!results || results.length === 0) {
          results = await stockApi.searchStocks(value).catch(() => []);
        }
        setSuggestions(results);
        setShowSug(true);
      } catch { /* 무시 */ }
      finally { setSearching(false); }
    }, 350);
  };

  const selectSuggestion = (s) => {
    setForm(f => ({ ...f, stockName: s.stockName, stockCode: s.stockCode || '' }));
    setSuggestions([]);
    setShowSug(false);
  };

  const handleSubmit = async () => {
    if (!form.companyId) return showToast('증권사를 선택하세요', 'error');
    if (!form.stockName.trim()) return showToast('주식명을 입력하세요', 'error');
    if (!form.quantity || Number(form.quantity) <= 0) return showToast('수량을 입력하세요', 'error');
    if (!form.avgPrice || Number(form.avgPrice) <= 0) return showToast('평균단가를 입력하세요', 'error');

    setLoading(true);
    try {
      await stockApi.saveHolding({
        snapshotDate: date,
        companyId: Number(form.companyId),
        stockCode: form.stockCode || null,
        stockName: form.stockName.trim(),
        quantity: Number(form.quantity),
        avgPrice: Number(form.avgPrice),
        currentPrice: form.currentPrice ? Number(form.currentPrice) : null,
        memo: form.memo || null,
      });
      showToast('잔고가 저장되었습니다.');
      onClose();
      onSaved();
    } catch {
      showToast('저장 실패', 'error');
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div className="modal modal-blur fade show" style={{ display: 'block' }} onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, onClose)}>
      <div className="modal-dialog modal-sm modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">잔고 입력 — {date}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="mb-2">
              <label className="form-label form-label-sm mb-1">증권사 <span className="text-danger">*</span></label>
              <select className="form-select form-select-sm"
                value={form.companyId}
                onChange={e => setForm(f => ({ ...f, companyId: e.target.value }))}>
                <option value="">-- 선택 --</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div className="mb-2" style={{ position: 'relative' }}>
              <label className="form-label form-label-sm mb-1">주식명 <span className="text-danger">*</span></label>
              <div style={{ position: 'relative' }}>
                <input type="text" className="form-control form-control-sm"
                  placeholder="삼성전자 (입력 시 자동 검색)"
                  value={form.stockName}
                  onChange={e => handleStockNameChange(e.target.value)}
                  onBlur={() => setTimeout(() => setShowSug(false), 200)}
                  onFocus={() => suggestions.length > 0 && setShowSug(true)}
                  style={{ paddingRight: searching ? 28 : undefined }}
                />
                {searching && (
                  <span style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    width: 14, height: 14, border: '2px solid #ccc', borderTopColor: '#206bc4',
                    borderRadius: '50%', display: 'inline-block',
                    animation: 'spin 0.7s linear infinite',
                  }} />
                )}
              </div>
              {showSug && suggestions.length > 0 && (
                <div style={{
                  position: 'absolute', zIndex: 9999, top: '100%', left: 0, right: 0,
                  background: '#fff', border: '1px solid #e6e7e9', borderRadius: 6,
                  boxShadow: '0 4px 12px rgba(0,0,0,.1)', maxHeight: 200, overflowY: 'auto',
                }}>
                  {suggestions.map((s, i) => (
                    <div key={i} style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
                      onMouseDown={() => selectSuggestion(s)}
                      className="dropdown-item">
                      <span style={{ flex: 1 }}>{s.stockName}</span>
                      {s.stockCode && <span style={{ color: '#888', fontSize: 11 }}>{s.stockCode}</span>}
                      {s.market && (
                        <span style={{
                          fontSize: 10, padding: '1px 5px', borderRadius: 3,
                          background: s.market === 'KOSPI' ? '#e8f0fe' : '#f0fce8',
                          color: s.market === 'KOSPI' ? '#1a56db' : '#166534',
                          fontWeight: 600,
                        }}>{s.market}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-2">
              <label className="form-label form-label-sm mb-1">
                종목코드&nbsp;
                <a href="https://finance.naver.com/" target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>
                  (네이버 금융)
                </a>
              </label>
              <input type="text" className="form-control form-control-sm"
                placeholder="자동 입력 또는 직접 입력"
                value={form.stockCode}
                onChange={e => setForm(f => ({ ...f, stockCode: e.target.value }))}
                maxLength={10}
              />
            </div>

            <div className="mb-2">
              <label className="form-label form-label-sm mb-1">보유수량 <span className="text-danger">*</span></label>
              <input type="number" className="form-control form-control-sm"
                min={0} value={form.quantity}
                onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
              />
            </div>

            <div className="mb-2">
              <label className="form-label form-label-sm mb-1">평균단가 <span className="text-danger">*</span></label>
              <input type="number" className="form-control form-control-sm"
                min={0} value={form.avgPrice}
                onChange={e => setForm(f => ({ ...f, avgPrice: e.target.value }))}
              />
            </div>

            {editData && (
              <div className="mb-2">
                <label className="form-label form-label-sm mb-1">
                  현재가&nbsp;<span style={{ color: '#888', fontWeight: 400, fontSize: 11 }}>(수동 입력 — stock_prices 저장)</span>
                </label>
                <input type="number" className="form-control form-control-sm"
                  min={0} placeholder="비워두면 기존 가격 유지"
                  value={form.currentPrice}
                  onChange={e => setForm(f => ({ ...f, currentPrice: e.target.value }))}
                />
              </div>
            )}

            <div className="mb-0">
              <label className="form-label form-label-sm mb-1">메모</label>
              <textarea className="form-control form-control-sm" rows={2}
                value={form.memo}
                onChange={e => setForm(f => ({ ...f, memo: e.target.value }))}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>취소</button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 예수금 입력 모달 ──────────────────────────── */
function DepositModal({ open, onClose, onSaved, showToast, companyId, companyName, date }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setAmount(''); }, [open]);

  const handleSubmit = async () => {
    if (!amount && amount !== 0) return showToast('예수금을 입력하세요', 'error');
    setLoading(true);
    try {
      await stockApi.saveDeposit({ companyId, recordDate: date, amount: Number(amount) });
      showToast('예수금이 저장되었습니다.');
      onClose();
      onSaved();
    } catch {
      showToast('저장 실패', 'error');
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div className="modal modal-blur fade show" style={{ display: 'block' }} onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, onClose)}>
      <div className="modal-dialog modal-sm modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">예수금 입력 — {companyName}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="mb-2">
              <label className="form-label form-label-sm mb-1">기준일</label>
              <input type="text" className="form-control form-control-sm" value={date} readOnly />
            </div>
            <div className="mb-0">
              <label className="form-label form-label-sm mb-1">예수금 (원) <span className="text-danger">*</span></label>
              <input type="number" className="form-control form-control-sm"
                min={0} value={amount} placeholder="0"
                onChange={e => setAmount(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>취소</button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 보유주식 파이차트 ──────────────────────────── */
const PIE_COLORS = [
  '#4dabf7','#f783ac','#69db7c','#ffd43b','#a9e34b','#74c0fc','#f59f00',
  '#63e6be','#da77f2','#ff8787','#339af0','#51cf66','#ff922b','#cc5de8',
  '#20c997','#fab005','#e64980','#94d82d','#15aabf','#fd7e14',
];

function PieChart({ items, title, valueKey }) {
  const size = 180;
  const cx = size / 2, cy = size / 2, r = 72, innerR = 36;
  const [hovered, setHovered] = useState(null);

  const total = items.reduce((s, it) => s + it[valueKey], 0);
  if (total <= 0) return null;

  let angle = -Math.PI / 2;
  const slices = items.map((it, i) => {
    const pct = it[valueKey] / total;
    const startAngle = angle;
    angle += pct * 2 * Math.PI;
    return { ...it, pct, startAngle, endAngle: angle, color: PIE_COLORS[i % PIE_COLORS.length], origIdx: i };
  });

  const arc = (sa, ea, outerR, innerR2) => {
    const x1 = cx + outerR * Math.cos(sa), y1 = cy + outerR * Math.sin(sa);
    const x2 = cx + outerR * Math.cos(ea), y2 = cy + outerR * Math.sin(ea);
    const ix1 = cx + innerR2 * Math.cos(ea), iy1 = cy + innerR2 * Math.sin(ea);
    const ix2 = cx + innerR2 * Math.cos(sa), iy2 = cy + innerR2 * Math.sin(sa);
    const large = ea - sa > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${large} 1 ${x2} ${y2} L ${ix1} ${iy1} A ${innerR2} ${innerR2} 0 ${large} 0 ${ix2} ${iy2} Z`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#555' }}>{title}</div>
      <svg width={size} height={size} style={{ overflow: 'visible' }}>
        {slices.map((sl, i) => {
          const isHov = hovered === i;
          const scale = isHov ? 1.06 : 1;
          const midA = (sl.startAngle + sl.endAngle) / 2;
          const tx = isHov ? Math.cos(midA) * 6 : 0;
          const ty = isHov ? Math.sin(midA) * 6 : 0;
          return (
            <path
              key={i}
              d={arc(sl.startAngle, sl.endAngle, r, innerR)}
              fill={sl.color}
              stroke="#fff"
              strokeWidth={1.5}
              transform={`translate(${tx},${ty}) scale(${scale})`}
              style={{ transformOrigin: `${cx}px ${cy}px`, cursor: 'pointer', transition: 'transform 0.15s' }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              <title>{sl.stockName}: {fmt(sl[valueKey])}원 ({(sl.pct * 100).toFixed(1)}%)</title>
            </path>
          );
        })}
        {hovered != null && slices[hovered] && (
          <text x={cx} y={cy - 7} textAnchor="middle" fontSize={9} fill="#555">{slices[hovered].stockName}</text>
        )}
        {hovered != null && slices[hovered] && (
          <text x={cx} y={cy + 7} textAnchor="middle" fontSize={10} fontWeight={700} fill="#333">
            {(slices[hovered].pct * 100).toFixed(1)}%
          </text>
        )}
        {hovered == null && (
          <text x={cx} y={cy + 5} textAnchor="middle" fontSize={11} fill="#888">비중</text>
        )}
      </svg>
      {/* 범례 */}
      <div style={{ marginTop: 10, width: '100%', maxWidth: 300 }}>
        {[...slices].sort((a, b) => b[valueKey] - a[valueKey]).map((sl, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '2px 0',
            fontWeight: hovered === sl.origIdx ? 700 : 400, color: hovered === sl.origIdx ? '#222' : '#555' }}
            onMouseEnter={() => setHovered(sl.origIdx)} onMouseLeave={() => setHovered(null)}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: sl.color, flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sl.stockName}</span>
            <span style={{ color: '#555', flexShrink: 0, marginRight: 4 }}>{(sl[valueKey] / 1000000).toFixed(1)}백만</span>
            <span style={{ color: '#aaa', flexShrink: 0, minWidth: 38, textAlign: 'right' }}>{(sl.pct * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 1. 수익률 분포 막대그래프 ──────────────────── */
function ReturnRateChart({ holdings }) {
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(400);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => setContainerW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const items = holdings
    .filter(h => Number(h.currentPrice) > 0 && Number(h.purchaseAmount) > 0)
    .map(h => ({ name: h.stockName, rate: Number(h.returnRate || 0) }))
    .sort((a, b) => b.rate - a.rate);

  if (items.length === 0) return null;

  const maxAbs = Math.max(...items.map(it => Math.abs(it.rate)), 1);
  const BAR_H = 28, GAP = 6, VAL_W = 55;
  const LABEL_W = Math.max(60, Math.min(150, Math.floor(containerW * 0.28)));
  const BAR_MAX = Math.max(80, Math.floor((containerW - LABEL_W - VAL_W) * 0.45));

  return (
    <div className="card h-100">
      <div className="card-header py-2 px-3">
        <div className="card-title mb-0" style={{ fontSize: 13 }}>종목별 수익률</div>
      </div>
      <div className="card-body" ref={containerRef}>
        <div>
          {items.map((it, i) => {
            const isPos = it.rate >= 0;
            const barW = Math.abs(it.rate) / maxAbs * BAR_MAX;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: GAP, height: BAR_H }}>
                <div style={{ width: LABEL_W, fontSize: 12, color: '#555', textAlign: 'right', paddingRight: 8,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}
                  title={it.name}>{it.name}</div>
                <div style={{ flex: 1, position: 'relative', height: BAR_H, display: 'flex', alignItems: 'center' }}>
                  <div style={{ position: 'absolute', left: BAR_MAX, top: 0, bottom: 0, width: 1, background: '#e0e0e0' }} />
                  <div style={{
                    position: 'absolute',
                    height: 18,
                    width: barW,
                    background: isPos ? '#4e9af1' : '#f87171',
                    borderRadius: isPos ? '0 3px 3px 0' : '3px 0 0 3px',
                    left: isPos ? BAR_MAX : BAR_MAX - barW,
                    transition: 'width 0.3s',
                  }} />
                </div>
                <div style={{ width: VAL_W, fontSize: 12, fontWeight: 600, flexShrink: 0, paddingLeft: 6,
                  color: isPos ? '#4e9af1' : '#f87171' }}>
                  {isPos ? '+' : ''}{it.rate.toFixed(1)}%
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── 2. 매수금액 vs 평가금액 비교 막대 ──────────── */
function PurchaseVsEvalChart({ holdings }) {
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(400);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => setContainerW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const items = holdings
    .filter(h => Number(h.purchaseAmount) > 0 && Number(h.currentPrice) > 0)
    .map(h => ({ name: h.stockName, purchase: Number(h.purchaseAmount), eval: Number(h.evalAmount || 0) }))
    .sort((a, b) => b.purchase - a.purchase);

  if (items.length === 0) return null;

  const maxVal = Math.max(...items.flatMap(it => [it.purchase, it.eval]), 1);
  const BAR_H = 10, GAP = 18;
  const LABEL_W = Math.max(60, Math.min(150, Math.floor(containerW * 0.28)));
  const BAR_MAX = Math.max(80, Math.floor((containerW - LABEL_W) * 0.55));

  const fmtM = v => (v / 1000000).toFixed(1) + '백만';

  return (
    <div className="card h-100">
      <div className="card-header py-2 px-3 d-flex align-items-center gap-3">
        <div className="card-title mb-0" style={{ fontSize: 13 }}>매수금액 vs 평가금액</div>
        <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#888' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#cbd5e1', borderRadius: 2, marginRight: 4 }} />매수</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#4e9af1', borderRadius: 2, marginRight: 4 }} />평가</span>
        </div>
      </div>
      <div className="card-body" ref={containerRef}>
        <div>
          {items.map((it, i) => {
            const pw = it.purchase / maxVal * BAR_MAX;
            const ew = it.eval / maxVal * BAR_MAX;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: GAP }}>
                <div style={{ width: LABEL_W, fontSize: 12, color: '#555', textAlign: 'right', paddingRight: 8,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}
                  title={it.name}>{it.name}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
                    <div style={{ width: pw, height: BAR_H, background: '#cbd5e1', borderRadius: '0 3px 3px 0', minWidth: 2 }} />
                    <span style={{ fontSize: 10, color: '#888', marginLeft: 4 }}>{fmtM(it.purchase)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: ew, height: BAR_H, background: '#4e9af1', borderRadius: '0 3px 3px 0', minWidth: 2 }} />
                    <span style={{ fontSize: 10, color: it.eval >= it.purchase ? '#4e9af1' : '#f87171', marginLeft: 4 }}>{fmtM(it.eval)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── 5. 주식별 보유수량 + 금액 테이블 ───────────── */
function StockHoldingsTable({ holdings }) {
  const fmt = (n) => Number(n || 0).toLocaleString();
  const items = holdings
    .filter(h => Number(h.quantity) > 0 || Number(h.evalAmount) > 0)
    .sort((a, b) => Number(b.evalAmount || 0) - Number(a.evalAmount || 0));

  if (items.length === 0) return null;

  const totalQty = items.reduce((s, h) => s + Number(h.quantity || 0), 0);
  const totalEval = items.reduce((s, h) => s + Number(h.evalAmount || 0), 0);
  const totalPurchase = items.reduce((s, h) => s + Number(h.purchaseAmount || 0), 0);

  return (
    <div className="card h-100">
      <div className="card-header py-2 px-3">
        <div className="card-title mb-0" style={{ fontSize: 13 }}>주식별 보유수량 &amp; 금액</div>
        <div className="card-subtitle" style={{ fontSize: 11, color: '#aaa' }}>평가금액 기준 내림차순</div>
      </div>
      <div className="card-body p-0" style={{ overflowY: 'auto' }}>
        <table className="table table-sm table-hover mb-0" style={{ fontSize: 12 }}>
          <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
            <tr>
              <th className="ps-3">종목명</th>
              <th className="text-end">수량</th>
              <th className="text-end">매수금액</th>
              <th className="text-end pe-3">평가금액</th>
            </tr>
          </thead>
          <tbody>
            {items.map((h, i) => (
              <tr key={i}>
                <td className="ps-3" style={{ fontWeight: 500 }}>{h.stockName}</td>
                <td className="text-end">{fmt(h.quantity)}</td>
                <td className="text-end">{fmt(h.purchaseAmount)}</td>
                <td className="text-end pe-3">{fmt(h.evalAmount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot style={{ borderTop: '2px solid #dee2e6', fontWeight: 700, background: '#f8f9fa' }}>
            <tr>
              <td className="ps-3">합계</td>
              <td className="text-end">{fmt(totalQty)}</td>
              <td className="text-end">{fmt(totalPurchase)}</td>
              <td className="text-end pe-3">{fmt(totalEval)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ── 증권사별 비중 파이차트 ──────────────────────── */
function BrokerPieChart({ companies }) {
  const items = companies
    .map(c => ({ name: c.companyName, value: Number(c.totalEvalAmount || 0) }))
    .filter(it => it.value > 0);

  const size = 180;
  const cx = size / 2, cy = size / 2, r = 72, innerR = 36;
  const [hovered, setHovered] = useState(null);

  const total = items.reduce((s, it) => s + it.value, 0);
  if (total <= 0) return null;

  let angle = -Math.PI / 2;
  const slices = items.map((it, i) => {
    const pct = it.value / total;
    const startAngle = angle;
    angle += pct * 2 * Math.PI;
    return { ...it, pct, startAngle, endAngle: angle, color: PIE_COLORS[i % PIE_COLORS.length], origIdx: i };
  });

  const arc = (sa, ea, outerR, innerR2) => {
    const x1 = cx + outerR * Math.cos(sa), y1 = cy + outerR * Math.sin(sa);
    const x2 = cx + outerR * Math.cos(ea), y2 = cy + outerR * Math.sin(ea);
    const ix1 = cx + innerR2 * Math.cos(ea), iy1 = cy + innerR2 * Math.sin(ea);
    const ix2 = cx + innerR2 * Math.cos(sa), iy2 = cy + innerR2 * Math.sin(sa);
    const large = ea - sa > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${large} 1 ${x2} ${y2} L ${ix1} ${iy1} A ${innerR2} ${innerR2} 0 ${large} 0 ${ix2} ${iy2} Z`;
  };

  return (
    <div className="card h-100">
      <div className="card-header py-2 px-3">
        <div className="card-title mb-0" style={{ fontSize: 13 }}>증권사별 비중</div>
      </div>
      <div className="card-body">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#555' }}>평가금액 기준</div>
          <svg width={size} height={size} style={{ overflow: 'visible' }}>
            {slices.map((sl, i) => {
              const isHov = hovered === i;
              const scale = isHov ? 1.06 : 1;
              const midA = (sl.startAngle + sl.endAngle) / 2;
              const tx = isHov ? Math.cos(midA) * 6 : 0;
              const ty = isHov ? Math.sin(midA) * 6 : 0;
              return (
                <path
                  key={i}
                  d={arc(sl.startAngle, sl.endAngle, r, innerR)}
                  fill={sl.color}
                  stroke="#fff"
                  strokeWidth={1.5}
                  transform={`translate(${tx},${ty}) scale(${scale})`}
                  style={{ transformOrigin: `${cx}px ${cy}px`, cursor: 'pointer', transition: 'transform 0.15s' }}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <title>{sl.name}: {fmt(sl.value)}원 ({(sl.pct * 100).toFixed(1)}%)</title>
                </path>
              );
            })}
            {hovered != null && slices[hovered] && (
              <text x={cx} y={cy - 7} textAnchor="middle" fontSize={9} fill="#555">{slices[hovered].name}</text>
            )}
            {hovered != null && slices[hovered] && (
              <text x={cx} y={cy + 7} textAnchor="middle" fontSize={10} fontWeight={700} fill="#333">
                {(slices[hovered].pct * 100).toFixed(1)}%
              </text>
            )}
            {hovered == null && (
              <text x={cx} y={cy + 5} textAnchor="middle" fontSize={11} fill="#888">비중</text>
            )}
          </svg>
          <div style={{ marginTop: 10, width: '100%', maxWidth: 300 }}>
            {[...slices].sort((a, b) => b.value - a.value).map((sl, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '2px 0',
                fontWeight: hovered === sl.origIdx ? 700 : 400, color: hovered === sl.origIdx ? '#222' : '#555' }}
                onMouseEnter={() => setHovered(sl.origIdx)} onMouseLeave={() => setHovered(null)}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: sl.color, flexShrink: 0 }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sl.name}</span>
                <span style={{ color: '#555', flexShrink: 0, marginRight: 4 }}>{(sl.value / 1000000).toFixed(1)}백만</span>
                <span style={{ color: '#aaa', flexShrink: 0, minWidth: 38, textAlign: 'right' }}>{(sl.pct * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HoldingsPieCharts({ holdings }) {
  // 같은 종목명끼리 합산
  const mergeByStock = (items, valueKey) => {
    const map = {};
    items.forEach(h => {
      if (!map[h.stockName]) map[h.stockName] = { stockName: h.stockName, [valueKey]: 0 };
      map[h.stockName][valueKey] += Number(h[valueKey] || 0);
    });
    return Object.values(map).filter(it => it[valueKey] > 0);
  };

  const purchaseItems = mergeByStock(
    holdings.filter(h => Number(h.purchaseAmount) > 0),
    'purchaseAmount'
  );
  const evalItems = mergeByStock(
    holdings.filter(h => Number(h.currentPrice) > 0 && Number(h.evalAmount) > 0),
    'evalAmount'
  );

  if (purchaseItems.length === 0 && evalItems.length === 0) return null;

  return (
    <div className="card mt-3">
      <div className="card-header py-2 px-3">
        <div className="card-title mb-0" style={{ fontSize: 13 }}>보유주식 비중</div>
      </div>
      <div className="card-body">
        <div className="row g-3 justify-content-center">
          {purchaseItems.length > 0 && <div className="col-12 col-md-6"><PieChart items={purchaseItems} title="매수금액 기준" valueKey="purchaseAmount" /></div>}
          {evalItems.length > 0 && <div className="col-12 col-md-6"><PieChart items={evalItems} title="평가금액 기준" valueKey="evalAmount" /></div>}
        </div>
      </div>
    </div>
  );
}

/* ── 오늘 시세로 환산 모달 ──────────────────────── */
function TodayPriceModal({ open, onClose, baseDate, holdings }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !holdings || holdings.length === 0) return;
    setLoading(true);
    setError('');
    setRows([]);

    const stockHoldings = holdings.filter(h => h.stockCode);
    if (stockHoldings.length === 0) {
      setLoading(false);
      setError('종목코드가 있는 주식이 없습니다.');
      return;
    }

    const codes = [...new Set(stockHoldings.map(h => h.stockCode))];
    stockApi.getCurrentPrices(codes)
      .then(priceMap => {
        const results = stockHoldings.map(h => {
          const todayPriceRaw = priceMap[h.stockCode];
          const todayPrice = todayPriceRaw != null ? Number(todayPriceRaw) : null;
          const qty = Number(h.quantity || 0);
          const avgPrice = Number(h.avgPrice || 0);
          const purchaseAmt = qty * avgPrice;
          const evalAmt = todayPrice != null ? qty * todayPrice : null;
          const profit = evalAmt != null ? evalAmt - purchaseAmt : null;
          const rate = purchaseAmt > 0 && profit != null ? profit / purchaseAmt * 100 : null;
          return {
            id: h.id,
            companyName: h.companyName,
            stockName: h.stockName,
            stockCode: h.stockCode,
            quantity: qty,
            avgPrice,
            baseDatePrice: Number(h.currentPrice || 0),
            todayPrice,
            evalAmt,
            profit,
            rate,
          };
        });
        setRows(results);
        setLoading(false);
      })
      .catch(() => {
        setError('현재 시세 조회에 실패했습니다.');
        setLoading(false);
      });
  }, [open, holdings]);

  if (!open) return null;

  const validRows = rows.filter(r => r.evalAmt != null);
  const totalEval = validRows.reduce((s, r) => s + r.evalAmt, 0);
  const totalPurchase = validRows.reduce((s, r) => s + r.quantity * r.avgPrice, 0);
  const totalProfit = totalEval - totalPurchase;
  const totalRate = totalPurchase > 0 ? totalProfit / totalPurchase * 100 : 0;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center gap-2">
                <IconCalculator size={18} />
                오늘 시세로 환산
                <span className="text-muted ms-2" style={{ fontSize: 13, fontWeight: 400 }}>
                  ({baseDate} 보유주식 기준)
                </span>
              </h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body p-0">
              {loading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-2" />
                  <div className="text-muted" style={{ fontSize: 13 }}>현재 시세 조회 중...</div>
                </div>
              )}
              {error && <div className="text-center py-4 text-muted">{error}</div>}
              {!loading && rows.length > 0 && (
                <div className="table-responsive" style={{ padding: 7 }}>
                  <table className="table table-vcenter table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>증권사</th>
                        <th>주식명</th>
                        <th className="text-end">수량</th>
                        <th className="text-end">평균단가</th>
                        <th className="text-end">{baseDate} 시세</th>
                        <th className="text-end">오늘 시세</th>
                        <th className="text-end">오늘 평가금액</th>
                        <th className="text-end">평가손익</th>
                        <th className="text-end">수익률</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.id || i}>
                          <td style={{ fontSize: 12, color: '#888' }}>{r.companyName}</td>
                          <td>
                            {r.stockName}
                            <span style={{ color: '#aaa', fontSize: 11, marginLeft: 4 }}>{r.stockCode}</span>
                          </td>
                          <td className="text-end">{fmt(r.quantity)}</td>
                          <td className="text-end">{fmt(r.avgPrice)}</td>
                          <td className="text-end" style={{ color: '#888' }}>
                            {r.baseDatePrice > 0 ? fmt(r.baseDatePrice) : <span style={{ color: '#ccc' }}>-</span>}
                          </td>
                          <td className="text-end" style={{ fontWeight: 600 }}>
                            {r.todayPrice != null ? fmt(r.todayPrice) : <span style={{ color: '#f59e0b' }}>조회실패</span>}
                          </td>
                          <td className="text-end">
                            {r.evalAmt != null ? fmt(r.evalAmt) : <span style={{ color: '#aaa' }}>-</span>}
                          </td>
                          <td className="text-end">
                            {r.profit != null ? diffEl(r.profit) : <span style={{ color: '#aaa' }}>-</span>}
                          </td>
                          <td className="text-end">
                            {r.rate != null ? pctEl(r.rate) : <span style={{ color: '#aaa' }}>-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {validRows.length > 0 && (
                      <tfoot>
                        <tr style={{ background: '#f0f5ff', fontWeight: 600 }}>
                          <td colSpan={6} className="text-muted" style={{ fontSize: 12 }}>합계</td>
                          <td className="text-end">{fmt(totalEval)}</td>
                          <td className="text-end">{diffEl(totalProfit)}</td>
                          <td className="text-end">{pctEl(totalRate)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <span className="text-muted me-auto" style={{ fontSize: 12 }}>
                * 종목코드 없는 종목은 조회에서 제외됩니다.
              </span>
              <button className="btn btn-secondary" onClick={onClose}>닫기</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
    </>
  );
}

/* ── 종목 테이블 ───────────────────────────────── */
function HoldingsTable({ holdings, showCompanyCol, onEdit, onDelete, onStockClick }) {
  // 현재가 있는 종목만 합계에 포함
  const pricedHoldings = holdings.filter(h => Number(h.currentPrice) > 0);
  const sumEval = pricedHoldings.reduce((s, h) => s + Number(h.evalAmount || 0), 0);
  const sumPurchase = pricedHoldings.reduce((s, h) => s + Number(h.purchaseAmount || 0), 0);
  const sumProfit = sumEval - sumPurchase;
  const sumRate = sumPurchase > 0 ? (sumProfit / sumPurchase * 100) : 0;
  const hasPriced = pricedHoldings.length > 0;

  if (holdings.length === 0) {
    return (
      <div className="text-muted text-center py-4" style={{ fontSize: 13 }}>
        데이터가 없습니다. 잔고를 입력하세요.
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-vcenter table-sm card-table">
        <thead>
          <tr>
            {showCompanyCol && <th style={{ width: 140 }}>증권사</th>}
            <th>주식명</th>
            <th className="text-end" style={{ width: 70 }}>수량</th>
            <th className="text-end" style={{ width: 100 }}>평균단가</th>
            <th className="text-end" style={{ width: 100 }}>현재가</th>
            <th className="text-end" style={{ width: 110 }}>평가금액</th>
            <th className="text-end" style={{ width: 110 }}>평가손익</th>
            <th className="text-end" style={{ width: 80 }}>수익률</th>
            <th style={{ width: 64 }}></th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((h, i) => (
            <tr key={h.id || i}>
              {showCompanyCol && (
                <td><span style={{ color: '#888', fontSize: 12 }}>{h.companyName}</span></td>
              )}
              <td>
                {h.stockCode
                  ? (
                    <span
                      style={{ cursor: 'pointer', color: '#206bc4' }}
                      onClick={() => onStockClick && onStockClick(h)}
                    >
                      {h.stockName} <span style={{ color: '#aaa', fontSize: 11 }}>{h.stockCode}</span>
                    </span>
                  )
                  : <span style={{ color: '#f59e0b', fontWeight: 600 }} title="종목코드 없음">{h.stockName}</span>
                }
                {(Number(h.per) > 0 || Number(h.pbr) > 0) && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: '#38bdf8' }}>
                    {Number(h.per) > 0 && <>PER {Number(h.per).toFixed(1)}</>}
                    {Number(h.per) > 0 && Number(h.pbr) > 0 && <span style={{ margin: '0 3px' }}>·</span>}
                    {Number(h.pbr) > 0 && <span style={{ color: '#a78bfa' }}>PBR {Number(h.pbr).toFixed(2)}</span>}
                  </span>
                )}
                {h.memo && <div style={{ color: '#aaa', fontSize: 11 }}>{h.memo}</div>}
              </td>
              <td className="text-end">{fmt(h.quantity)}</td>
              <td className="text-end">{fmt(h.avgPrice)}</td>
              <td className="text-end">
                {Number(h.currentPrice) > 0
                  ? <span title={h.priceDate ? `기준: ${h.priceDate}` : ''}>{fmt(h.currentPrice)}</span>
                  : <span style={{ color: '#aaa' }}>-</span>
                }
              </td>
              <td className="text-end">
                {Number(h.currentPrice) > 0 ? fmt(h.evalAmount) : <span style={{ color: '#aaa' }}>-</span>}
              </td>
              <td className="text-end">
                {Number(h.currentPrice) > 0 ? diffEl(h.profitLoss) : <span style={{ color: '#aaa' }}>-</span>}
              </td>
              <td className="text-end">
                {Number(h.currentPrice) > 0 ? pctEl(h.returnRate) : <span style={{ color: '#aaa' }}>-</span>}
              </td>
              <td>
                <div className="d-flex gap-1 justify-content-end">
                  <button className="btn btn-ghost-secondary btn-icon btn-sm" onClick={() => onEdit(h)}>
                    <IconPencil size={14} />
                  </button>
                  <button className="btn btn-ghost-danger btn-icon btn-sm" onClick={() => onDelete(h.id)}>
                    <IconTrash size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f0f5ff', fontWeight: 600 }}>
            <td colSpan={showCompanyCol ? 5 : 4} className="text-muted" style={{ fontSize: 12 }}>합계</td>
            <td className="text-end">{hasPriced ? fmt(sumEval) : <span style={{ color: '#aaa' }}>-</span>}</td>
            <td className="text-end">{hasPriced ? diffEl(sumProfit) : <span style={{ color: '#aaa' }}>-</span>}</td>
            <td className="text-end">{hasPriced ? pctEl(sumRate) : <span style={{ color: '#aaa' }}>-</span>}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/* ── 메인 페이지 ───────────────────────────────── */
export default function StockLedgerPage({ me, showToast }) {
  const today = getKSTDate();
  const [date, setDate] = useState(today);
  const [data, setData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [availableDates, setAvailableDates] = useState(new Set());
  const [noPriceDates, setNoPriceDates] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [showFetchOverlay, setShowFetchOverlay] = useState(false);
  const [marketIndex, setMarketIndex] = useState({});

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [holdingModal, setHoldingModal] = useState({ open: false, editData: null });
  const [depositModal, setDepositModal] = useState({ open: false, companyId: null, companyName: '' });
  const [memoTimelineOpen, setMemoTimelineOpen] = useState(false);
  const [tradeHistoryOpen, setTradeHistoryOpen] = useState(false);
  const [todayPriceOpen, setTodayPriceOpen] = useState(false);
  const [detailStock, setDetailStock] = useState(null);

  /* 종목명 클릭 시 ETF 여부를 먼저 확인해 [KRX데이터] ETF 상세 레이어 또는 [시장분석] 종목 상세 레이어를 띄운다 */
  const handleStockClick = async (h) => {
    if (!h.stockCode) return;
    try {
      const res = await api.get(`/krx/etf/${h.stockCode}/exists`);
      if (res.data === true) {
        setDetailStock({ stockCode: h.stockCode, stockName: h.stockName, isEtf: true });
        return;
      }
    } catch {
      // 확인 실패 시 기본(주식) 상세 레이어로 폴백
    }
    setDetailStock({ stockCode: h.stockCode, stockName: h.stockName });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [holdings, comps, dates, noDates, idx] = await Promise.all([
        stockApi.getHoldings(date),
        stockApi.getCompanies(),
        stockApi.getAvailableDates(),
        stockApi.getNoPriceDates(),
        stockApi.getMarketIndex(date).catch(() => ({})),
      ]);
      setData(holdings);
      setCompanies(comps);
      setAvailableDates(new Set(dates));
      setNoPriceDates(new Set(noDates));
      setMarketIndex(idx || {});
    } catch {
      showToast('데이터 로딩 실패', 'error');
    } finally { setLoading(false); }
  }, [date, showToast]);

  useEffect(() => { load(); }, [load]);

  const handleFetchPrices = () => {
    window.__fetchDate = date;
    setShowFetchOverlay(true);
    setFetchingPrices(true);
  };

  const handleFetchOverlayClose = (result) => {
    setShowFetchOverlay(false);
    setFetchingPrices(false);
    if (result) {
      showToast(`종가 수집 완료 — 성공 ${result.success}건 / 실패 ${result.fail}건`);
      load();
    }
  };

  const handleDeleteByDate = async () => {
    if (!window.confirm(`${date} 날짜의 잔고, 예수금, 종가 데이터를 모두 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`)) return;
    try {
      const result = await stockApi.deleteByDate(date);
      showToast(`삭제 완료 — 잔고 ${result.holdings}건 / 예수금 ${result.deposits}건 / 종가 ${result.prices}건`);
      load();
    } catch {
      showToast('삭제 실패', 'error');
    }
  };

  const handleCopy = async () => {
    const hasData = data.some(c => c.holdings && c.holdings.length > 0);
    if (hasData) {
      showToast('[날짜삭제] 버튼으로 삭제 후 실행 해주세요.', 'warning');
      return;
    }
    try {
      const result = await stockApi.copyFromPrevious(date);
      if (result.copied === 0) {
        showToast('복사할 이전 잔고가 없거나 이미 모두 존재합니다.', 'warning');
      } else {
        showToast(`이전 잔고 ${result.copied}건 복사 완료`);
        load();
      }
    } catch {
      showToast('복사 실패', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 잔고를 삭제하시겠습니까?')) return;
    try {
      await stockApi.deleteHolding(id);
      showToast('삭제되었습니다.');
      load();
    } catch {
      showToast('삭제 실패', 'error');
    }
  };

  const openEdit = (holding) => {
    setHoldingModal({ open: true, editData: holding });
  };

  /* ── 합계 계산 ── */
  const totalEval = data.reduce((s, c) => s + Number(c.totalEvalAmount || 0), 0);
  const totalDeposit = data.reduce((s, c) => s + Number(c.deposit || 0), 0);
  const totalAsset = totalEval + totalDeposit;
  const totalPurchase = data.reduce((s, c) =>
    s + (c.holdings || []).reduce((ss, h) => ss + Number(h.purchaseAmount || 0), 0), 0);
  const totalProfit = totalEval - totalPurchase;

  /* ── 전체 종목 (모든 증권사 합산) ── */
  const allHoldings = data.flatMap(c => (c.holdings || []).map(h => ({ ...h, companyName: c.companyName })));

  /* ── 현재 탭 데이터 ── */
  const currentCompany = data.find(c => String(c.companyId) === activeTab);

  return (
    <div className="container-xl py-4">
      {/* 페이지 헤더 */}
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <div className="page-pretitle">자산</div>
            <h2 className="page-title d-flex align-items-center gap-2">
              <IconChartBar size={24} />
              주식장부
            </h2>
          </div>
          <div className="col-auto d-flex gap-2 align-items-center flex-wrap">
            {/* 기준일 */}
            <StockDatePicker
              value={date}
              onChange={setDate}
              availableDates={availableDates}
              noPriceDates={noPriceDates}
            />

            <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
              onClick={load} disabled={loading}>
              <IconRefresh size={14} className={loading ? 'rotating' : ''} />
              새로고침
            </button>
            {date < today && (
              <button className="btn btn-sm btn-outline-info d-flex align-items-center gap-1"
                onClick={() => setTodayPriceOpen(true)}>
                <IconCalculator size={14} />
                오늘 시세로 환산
              </button>
            )}
            {!data.some(c => c.holdings && c.holdings.length > 0) && (
              <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                onClick={handleCopy}>
                <IconCopy size={14} />
                이전 잔고 복사
              </button>
            )}
            <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
              onClick={handleFetchPrices} disabled={fetchingPrices}>
              <IconRefresh size={14} className={fetchingPrices ? 'rotating' : ''} />
              {fetchingPrices ? '수집 중...' : '종가 수집'}
            </button>
            <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={() => setSettingsOpen(true)}>
              <IconSettings size={14} />
              증권사 설정
            </button>
            <button className="btn btn-sm btn-primary d-flex align-items-center gap-1"
              onClick={() => setHoldingModal({ open: true, editData: null })}>
              <IconPlus size={14} />
              잔고 입력
            </button>
            <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
              onClick={handleDeleteByDate}>
              <IconTrash size={14} />
              날짜 삭제
            </button>
          </div>
        </div>
      </div>

      {/* 전체 합계 카드 + 지수 카드 (6개 동일 디자인) */}
      <div className="row g-3 mb-3">
        <div className="col-6 col-md-2">
          <div className="card" style={{ background: '#1a1a2e', border: 'none' }}>
            <div className="card-body py-3">
              <div style={{ color: '#aaa', fontSize: 12, marginBottom: 4 }}>보유주식 합계</div>
              <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>{fmt(totalEval)}<span style={{ fontSize: 11, color: '#aaa', marginLeft: 3 }}>원</span></div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card" style={{ background: '#16213e', border: 'none' }}>
            <div className="card-body py-3">
              <div style={{ color: '#aaa', fontSize: 12, marginBottom: 4 }}>예수금 합계</div>
              <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>{fmt(totalDeposit)}<span style={{ fontSize: 11, color: '#aaa', marginLeft: 3 }}>원</span></div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card" style={{ background: '#0f3460', border: 'none' }}>
            <div className="card-body py-3">
              <div style={{ color: '#aaa', fontSize: 12, marginBottom: 4 }}>총 자산</div>
              <div style={{ color: '#e94560', fontSize: 20, fontWeight: 800 }}>{fmt(totalAsset)}<span style={{ fontSize: 11, color: '#aaa', marginLeft: 3 }}>원</span></div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card" style={{ background: '#162032', border: 'none' }}>
            <div className="card-body py-3">
              <div style={{ color: '#aaa', fontSize: 12, marginBottom: 4 }}>총 평가손익</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: totalProfit >= 0 ? '#e53e3e' : '#3182ce' }}>
                {totalProfit >= 0 ? '+' : '-'}{fmt(Math.abs(totalProfit))}<span style={{ fontSize: 11, color: '#aaa', marginLeft: 3 }}>원</span>
              </div>
            </div>
          </div>
        </div>
        {[
          { label: '코스피', key: 'kospi', bg: '#0d2137' },
          { label: '코스닥', key: 'kosdaq', bg: '#1a1035' },
        ].map(({ label, key, bg }) => {
          const val = marketIndex[key] != null ? Number(marketIndex[key]) : null;
          const chg = marketIndex[key + 'Change'] != null ? Number(marketIndex[key + 'Change']) : null;
          const chgColor = chg == null ? '#aaa' : chg >= 0 ? '#e53e3e' : '#3182ce';
          return (
            <div key={key} className="col-6 col-md-2">
              <div className="card" style={{ background: bg, border: 'none' }}>
                <div className="card-body py-3">
                  <div style={{ color: '#aaa', fontSize: 12, marginBottom: 4 }}>{label}</div>
                  {val != null ? (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>
                        {val.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      {chg != null && (
                        <span style={{ fontSize: 12, color: chgColor, fontWeight: 600 }}>
                          {chg >= 0 ? '▲' : '▼'} {Math.abs(chg).toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: '#555', fontSize: 13 }}>수집 전</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 날짜별 메모 */}
      <DailyMemoBox date={date} showToast={showToast} onTimelineOpen={() => setMemoTimelineOpen(true)} />

      {/* 거래내역 */}
      <DailyTradeBox date={date} hasHoldings={availableDates.has(date)} onAllTradeOpen={() => setTradeHistoryOpen(true)} companyId={activeTab !== 'all' ? Number(activeTab) : null} />

      {/* 자산 추이 차트 */}
      <AssetChart showToast={showToast} />

      {/* 탭 카드 */}
      <div className="card">
        <div className="card-header" style={{ padding: '10px 14px', borderBottom: '1px solid #e6e7e9' }}>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', flexWrap: 'nowrap', paddingBottom: 2 }}>
            {[{ id: 'all', name: '전체', amount: totalAsset, deposit: totalDeposit }, ...data.map(c => ({ id: String(c.companyId), name: c.companyName, amount: c.totalAsset, deposit: c.deposit, companyId: c.companyId }))].map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flexShrink: 0,
                    border: isActive ? '2px solid #206bc4' : '2px solid #e6e7e9',
                    borderRadius: 8,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    background: isActive ? '#206bc4' : '#f8f9fa',
                    color: isActive ? '#fff' : '#555',
                    fontWeight: isActive ? 700 : 400,
                    fontSize: 13,
                    lineHeight: 1.4,
                    transition: 'all .15s',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <span>{tab.name}</span>
                    {tab.companyId && (
                      <span
                        title="예수금 입력"
                        onClick={e => { e.stopPropagation(); setDepositModal({ open: true, companyId: tab.companyId, companyName: tab.name }); }}
                        style={{ cursor: 'pointer', opacity: 0.7, lineHeight: 1, padding: '1px 2px' }}
                      >
                        <IconCurrencyWon size={12} />
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, opacity: isActive ? 0.85 : 0.7, fontWeight: 600 }}>{fmt(tab.amount)}</div>
                  <div style={{ fontSize: 10, opacity: isActive ? 0.7 : 0.55, marginTop: 1 }}>
                    예{'\u00A0'}{fmt(tab.deposit)} 원
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="card-body p-3">
          {loading && (
            <div className="text-center text-muted d-flex flex-column align-items-center justify-content-center"
              style={{ minHeight: 320 }}>
              <div className="spinner-border spinner-border-sm text-secondary mb-2" />
              <div style={{ fontSize: 13 }}>로딩 중...</div>
            </div>
          )}

          {!loading && activeTab === 'all' && (
            <>
              <HoldingsTable
                holdings={allHoldings}
                showCompanyCol={true}
                onEdit={openEdit}
                onDelete={handleDelete}
                onStockClick={handleStockClick}
              />
              <HoldingsPieCharts holdings={allHoldings} />
              {(() => {
                // 같은 종목명끼리 합산 (증권사 무관)
                const mergedMap = {};
                allHoldings.forEach(h => {
                  const key = h.stockName;
                  if (!mergedMap[key]) {
                    mergedMap[key] = { ...h, purchaseAmount: 0, evalAmount: 0, profitLoss: 0, quantity: 0 };
                  }
                  const m = mergedMap[key];
                  m.purchaseAmount = Number(m.purchaseAmount) + Number(h.purchaseAmount || 0);
                  m.evalAmount     = Number(m.evalAmount)     + Number(h.evalAmount     || 0);
                  m.profitLoss     = Number(m.profitLoss)     + Number(h.profitLoss     || 0);
                  m.quantity       = Number(m.quantity)       + Number(h.quantity       || 0);
                });
                const mergedHoldings = Object.values(mergedMap).map(m => ({
                  ...m,
                  returnRate: m.purchaseAmount > 0 ? (m.profitLoss / m.purchaseAmount * 100) : 0,
                  currentPrice: m.evalAmount > 0 ? 1 : 0,
                }));
                return (
                  <>
                    {/* 주식별 보유수량&금액 + 증권사별 비중 한 줄 */}
                    <div className="row g-3 mt-0 align-items-stretch">
                      <div className="col-12 col-md-6"><StockHoldingsTable holdings={mergedHoldings} /></div>
                      {data.length > 1 && <div className="col-12 col-md-6"><BrokerPieChart companies={data} /></div>}
                    </div>
                    {/* 종목별 수익률 + 매수 vs 평가 한 줄 */}
                    <div className="row g-3 mt-0">
                      <div className="col-12 col-md-6"><ReturnRateChart holdings={mergedHoldings} /></div>
                      <div className="col-12 col-md-6"><PurchaseVsEvalChart holdings={mergedHoldings} /></div>
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {!loading && activeTab !== 'all' && currentCompany && (
            <>
              {/* 증권사 소계 카드 */}
              <div className="row g-3 mb-3">
                <div className="col-6 col-md-3">
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="text-muted mb-1" style={{ fontSize: 12 }}>평가금액</div>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>{fmt(currentCompany.totalEvalAmount)} 원</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card card-sm">
                    <div className="card-body d-flex align-items-center justify-content-between">
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>예수금</div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>{fmt(currentCompany.deposit)} 원</div>
                      </div>
                      <button className="btn btn-ghost-secondary btn-icon btn-sm"
                        onClick={() => setDepositModal({ open: true, companyId: currentCompany.companyId, companyName: currentCompany.companyName })}>
                        <IconCurrencyWon size={16} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="text-muted mb-1" style={{ fontSize: 12 }}>증권사 총액</div>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>{fmt(currentCompany.totalAsset)} 원</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="text-muted mb-1" style={{ fontSize: 12 }}>평가손익</div>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>
                        {diffEl(Number(currentCompany.totalEvalAmount) -
                          (currentCompany.holdings || []).reduce((s, h) => s + Number(h.purchaseAmount || 0), 0))}
                        <span style={{ fontSize: 12, color: '#aaa', marginLeft: 4 }}>원</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <HoldingsTable
                holdings={currentCompany.holdings || []}
                showCompanyCol={false}
                onEdit={openEdit}
                onDelete={handleDelete}
                onStockClick={handleStockClick}
              />
              <HoldingsPieCharts holdings={currentCompany.holdings || []} />
            </>
          )}

          {!loading && activeTab !== 'all' && !currentCompany && (
            <div className="text-muted text-center py-4">증권사 데이터를 불러올 수 없습니다.</div>
          )}

          {!loading && data.length === 0 && activeTab === 'all' && (
            <div className="text-center py-5">
              <IconChartBar size={48} style={{ opacity: 0.2, marginBottom: 8 }} />
              <div className="text-muted" style={{ fontSize: 13 }}>
                증권사를 먼저 추가하고 잔고를 입력하세요.
              </div>
              <button className="btn btn-sm btn-outline-primary mt-3"
                onClick={() => setSettingsOpen(true)}>
                <IconSettings size={14} className="me-1" />
                증권사 설정
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 모달들 */}
      <CompanySettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        showToast={showToast}
        onChanged={load}
      />
      <HoldingModal
        open={holdingModal.open}
        editData={holdingModal.editData}
        companies={companies}
        date={date}
        onClose={() => setHoldingModal({ open: false, editData: null })}
        onSaved={load}
        showToast={showToast}
      />
      <DepositModal
        open={depositModal.open}
        companyId={depositModal.companyId}
        companyName={depositModal.companyName}
        date={date}
        onClose={() => setDepositModal({ open: false, companyId: null, companyName: '' })}
        onSaved={load}
        showToast={showToast}
      />

      {/* 오늘 시세로 환산 모달 */}
      <TodayPriceModal
        open={todayPriceOpen}
        onClose={() => setTodayPriceOpen(false)}
        baseDate={date}
        holdings={data.flatMap(c => (c.holdings || []).map(h => ({ ...h, companyName: c.companyName })))}
      />

      {/* 종가수집 오버레이 */}
      {showFetchOverlay && <FetchProgressOverlay onClose={handleFetchOverlayClose} />}

      {/* 거래내역 전체보기 모달 */}
      <TradeHistoryModal
        open={tradeHistoryOpen}
        onClose={() => setTradeHistoryOpen(false)}
        onDateClick={setDate}
        companyId={activeTab !== 'all' ? Number(activeTab) : null}
      />

      {/* 메모 타임라인 모달 */}
      <MemoTimelineModal
        open={memoTimelineOpen}
        onClose={() => setMemoTimelineOpen(false)}
        onDateClick={setDate}
      />

      {/* 종목 상세 레이어 ([자산>시장분석]과 동일 컴포넌트 재사용) */}
      {detailStock && (
        detailStock.isEtf
          ? <KrxDataDetailModal stock={detailStock} onClose={() => setDetailStock(null)} />
          : <ScreeningDetailModal stock={detailStock} onClose={() => setDetailStock(null)} />
      )}

      {/* 모달 backdrop */}
      {(settingsOpen || holdingModal.open || depositModal.open) && (
        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
      )}

      <style>{`
        .rotating { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .modal { z-index: 1050; }
      `}</style>
    </div>
  );
}
